import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import type { Checkin } from '@awwyeah/shared';
import { checkinPostPath, shouldStampSeen } from '@awwyeah/shared';
import { formatClock } from '../feed';
import type { MemberDirectory } from '../members';
import { Avatar } from './Avatar';
import { CheckinPhoto } from './CheckinPhoto';
import { PlaceRow } from './PlacePin';
import { SeenByRow } from './SeenByRow';

/**
 * One check-in in the feed (design: "Feed — newest first").
 *
 * Photos run full-width with a 1px frame; text is big enough to read across a kitchen. A
 * place appears as its own row under the photo. Tapping the photo opens *that* photo and
 * nothing else — no next/previous, no "more from Kora".
 *
 * There is no like button, no comment affordance and no count anywhere below, and that is
 * the point of the screen.
 */

export function CheckinPost({
  checkin,
  members,
  memberCount,
  selfId,
  queued,
  onSeen,
}: {
  checkin: Checkin;
  members: MemberDirectory;
  memberCount: number;
  selfId: string | null;
  /** True while this post's photo is still only on this device. */
  queued: boolean;
  onSeen: (checkinId: string) => void;
}): JSX.Element {
  const author = members.get(checkin.authorId);
  const ref = useSeenObserver(checkin, selfId, onSeen);

  const subtitle = queued
    ? 'Saved here · will post when there’s signal'
    : [formatClock(checkin.createdAt), checkin.place?.name].filter(Boolean).join(' · ');

  return (
    <article
      ref={ref}
      data-testid="checkin-post"
      data-checkin-id={checkin.id}
      className="flex flex-col gap-2.5 border-y border-line bg-surface px-4 pb-3.5 pt-3"
    >
      <div className="flex items-center gap-2.5">
        <Avatar member={author} size={32} />
        <div className="min-w-0 flex-1">
          <div className="text-[15px] font-semibold text-fg">{author.name}</div>
          <div className="truncate text-xs text-fg-3">{subtitle}</div>
        </div>
        {queued && (
          <span
            className="h-2 w-2 flex-shrink-0 rounded-full bg-sync-off"
            title="Waiting for signal"
            aria-label="Waiting for signal"
          />
        )}
      </div>

      {checkin.photoRef && (
        <Link
          to={checkinPostPath(checkin.id)}
          className="block"
          aria-label={`Open ${author.name}’s photo`}
        >
          <CheckinPhoto
            photoRef={checkin.photoRef}
            alt={checkin.text ?? `${author.name}’s photo`}
            variant="display"
            srcSetOriginal
          />
        </Link>
      )}

      {checkin.place && <PlaceRow place={checkin.place} color={author.color} />}

      {checkin.text && (
        <p
          className={
            checkin.photoRef
              ? 'm-0 text-base leading-[1.45] text-fg'
              : 'm-0 text-[17px] font-medium leading-[1.4] text-fg'
          }
        >
          {checkin.text}
        </p>
      )}

      <SeenByRow checkin={checkin} members={members} memberCount={memberCount} />
    </article>
  );
}

/**
 * Stamps `seenBy` when the post has actually been on screen — half of it visible for a
 * beat, not merely scrolled past at speed. "Seen" should mean seen.
 *
 * Throttled to once per post per session by the ref guard, and to once *ever* by the
 * shared `shouldStampSeen` rule (which also refuses to stamp your own posts), so a feed
 * that is scrolled up and down all morning writes to the doc exactly zero extra times.
 */
const SEEN_DWELL_MS = 900;

function useSeenObserver(
  checkin: Checkin,
  selfId: string | null,
  onSeen: (checkinId: string) => void,
): (node: HTMLElement | null) => void {
  const stamped = useRef(false);
  const nodeRef = useRef<HTMLElement | null>(null);
  const eligible = selfId !== null && shouldStampSeen(checkin, selfId);
  const eligibleRef = useRef(eligible);
  eligibleRef.current = eligible;

  useEffect(() => {
    const node = nodeRef.current;
    if (!node || !eligible || stamped.current) return;
    if (typeof IntersectionObserver === 'undefined') return;

    let timer: ReturnType<typeof setTimeout> | null = null;
    const observer = new IntersectionObserver(
      (records) => {
        const visible = records.some((record) => record.isIntersecting);
        if (visible && timer === null) {
          timer = setTimeout(() => {
            if (!stamped.current && eligibleRef.current) {
              stamped.current = true;
              onSeen(checkin.id);
            }
            observer.disconnect();
          }, SEEN_DWELL_MS);
        } else if (!visible && timer !== null) {
          clearTimeout(timer);
          timer = null;
        }
      },
      { threshold: 0.5 },
    );

    observer.observe(node);
    return () => {
      if (timer !== null) clearTimeout(timer);
      observer.disconnect();
    };
  }, [checkin.id, eligible, onSeen]);

  return (node: HTMLElement | null) => {
    nodeRef.current = node;
  };
}
